/**
 * Tenant-scoped localStorage utility.
 *
 * Prefixes all keys with `{tenantId}:` so switching tenants
 * never leaks preferences (e.g. category filters) across tenants.
 */

const TENANT_PREFIX = (tenantId: string, key: string) => `${tenantId}:${key}`;

/**
 * Read a tenant-scoped value from localStorage.
 * Returns null if the key doesn't exist.
 */
export function getTenantItem(key: string, tenantId: string): string | null {
  return localStorage.getItem(TENANT_PREFIX(tenantId, key));
}

/**
 * Write a tenant-scoped value to localStorage.
 */
export function setTenantItem(key: string, value: string, tenantId: string): void {
  localStorage.setItem(TENANT_PREFIX(tenantId, key), value);
}

/**
 * Remove a tenant-scoped value from localStorage.
 */
export function removeTenantItem(key: string, tenantId: string): void {
  localStorage.removeItem(TENANT_PREFIX(tenantId, key));
}

/**
 * Migrate a legacy (un-prefixed) key to tenant-scoped storage.
 * If the old key exists and the new key doesn't, copies the value
 * and removes the old key. Safe to call repeatedly.
 */
export function migrateLegacyKey(key: string, tenantId: string): void {
  const oldValue = localStorage.getItem(key);
  if (oldValue !== null && getTenantItem(key, tenantId) === null) {
    setTenantItem(key, oldValue, tenantId);
  }
  // Always clean up the old un-prefixed key
  if (oldValue !== null) {
    localStorage.removeItem(key);
  }
}
